import { useCallback, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import AttachmentItem, {
  AttachmentDisplayItem,
} from '@/components/Common/AttachmentList/AttachmentItem';
import { ListRoot } from '@/components/Common/AttachmentList/styles';
import FileUpload, {
  FileUploadRejection,
  FileUploadValidationError,
} from '@/components/Common/FileUpload';
import { FieldCheckLabel } from '@/components/Common/FileUpload/styles';
import { ATTACHMENT_MAX_SIZE } from '@/constant/approval';
import useDownloadUrl from '@/hooks/approval/useDownloadUrl';
import useFileUpload, {
  AttachmentUploadError,
} from '@/hooks/approval/useFileUpload';
import useFileUploadStatus from '@/hooks/useFileUploadStatus';
import { getAttachmentKey, type Attachment } from '@/types/approval';
import { approvalCreateUploadingAttachmentsAtom } from './atoms';
import { AttachmentBlock } from './styles';
import type { ApprovalFormValues } from './types';

const IMG_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp']);

const getExt = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx < 0 ? '' : name.slice(idx + 1).toLowerCase();
};

const validationMessage = (
  reason: FileUploadValidationError,
  t: (key: string) => string,
): string => {
  switch (reason) {
    case 'EMPTY_FILE':
      return t('0바이트 파일은 업로드할 수 없습니다.');
    case 'OVER_MAX_SIZE':
      return t('파일당 최대 500MB까지 업로드 가능합니다.');
    case 'NAME_TOO_LONG':
      return t('파일명이 200자를 초과합니다.');
    case 'INVALID_EXTENSION':
      return t('허용되지 않는 형식입니다.');
    default:
      return t('업로드에 실패했습니다.');
  }
};

type AttachmentRowProps = {
  attachment: Attachment;
  onDelete: (fileId: string) => void;
};

/**
 * 첨부 항목 다운로드 URL 주입 — 신규 업로드(fileUuid)는 useDownloadUrl 조회,
 * 편집 모드 기존 첨부는 detail 응답의 urlOrg 사용. url 도착 전엔 확장자 아이콘 폴백.
 */
const AttachmentRow = ({ attachment, onDelete }: AttachmentRowProps) => {
  const { data } = useDownloadUrl(attachment.fileUuid);
  const ext = (
    attachment.fileFormat || getExt(attachment.fileName)
  ).toLowerCase();
  const url = data?.url ?? attachment.urlOrg ?? undefined;
  const isImage = IMG_EXTS.has(ext) || attachment.mimeType === 'image';
  const item: AttachmentDisplayItem = {
    fileId: getAttachmentKey(attachment),
    name: attachment.fileName,
    size: attachment.fileSize,
    uploadedAt: attachment.uploadDate,
    ext,
    thumbnailUrl: isImage ? url : undefined,
    url,
  };
  return <AttachmentItem item={item} mode="edit" onDelete={onDelete} />;
};

const AttachmentField = () => {
  const { t } = useTranslation();
  const { control, watch, setValue, getValues } =
    useFormContext<ApprovalFormValues>();
  const attachments = watch('attachments');
  // AI 업로더(205 등)가 올리는 중인 파일 — 레거시처럼 목록 끝에 업로드 중 항목
  const uploadingItems = useAtomValue(approvalCreateUploadingAttachmentsAtom);
  const { upload } = useFileUpload('default');
  const { addUploading, addError, setSuccess, setError, clearAll } =
    useFileUploadStatus();

  // 이 컴포넌트가 언마운트될 때(제출 성공→조회 전환, 모달 닫기, 페이지 이탈 전부 해당)
  // 업로드 상태 모달을 비운다. 모달이 글로벌이라 여기서 안 지우면
  // 조회화면 위에 '업로드 완료'가 계속 남는다.
  useEffect(() => clearAll, [clearAll]);

  const handleFilesAdded = useCallback(
    (files: File[]) => {
      // jQuery File Upload add 콜백처럼 각 파일별 독립 진행. await 직렬화 안 함.
      files.forEach(async (file) => {
        const id = addUploading(file.name, file);
        try {
          const result = await upload(file);
          setSuccess(id);
          const next: Attachment = {
            fileUuid: result.uuid,
            fileName: result.fileName ?? file.name,
            fileSize: result.fileSizeInBytes ?? file.size,
            fileFormat: getExt(result.fileName ?? file.name),
            uploadDate: result.createdAt,
            urlOrg: '',
          };
          setValue('attachments', [...getValues('attachments'), next], {
            shouldDirty: true,
          });
        } catch (err) {
          // AttachmentUploadError 는 번역 키를 따로 실어 온다 (message 는 한국어 원문)
          const message =
            err instanceof AttachmentUploadError && err.i18n
              ? t(err.i18n.key, err.i18n.options)
              : t('업로드에 실패했습니다.');
          setError(id, message);
        }
      });
    },
    [addUploading, getValues, setError, setSuccess, setValue, t, upload],
  );

  const handleValidationRejection = useCallback(
    (rejections: FileUploadRejection[]) => {
      rejections.forEach((r) => {
        addError(r.file.name, validationMessage(r.reason, t));
      });
    },
    [addError, t],
  );

  const handleDelete = useCallback(
    (key: string) => {
      setValue(
        'attachments',
        getValues('attachments').filter((a) => getAttachmentKey(a) !== key),
        { shouldDirty: true },
      );
    },
    [getValues, setValue],
  );

  return (
    <AttachmentBlock>
      <FileUpload
        variant="default"
        multiple
        maxSize={ATTACHMENT_MAX_SIZE}
        headerRight={
          <Controller
            name="showImagesBelowContent"
            control={control}
            render={({ field }) => (
              <FieldCheckLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t('첨부 이미지 문서 하단에 표시')}
              />
            )}
          />
        }
        onFilesAdded={handleFilesAdded}
        onError={handleValidationRejection}
      />
      {attachments.length + uploadingItems.length > 0 && (
        <ListRoot>
          {attachments.map((a) => (
            <AttachmentRow
              key={getAttachmentKey(a)}
              attachment={a}
              onDelete={handleDelete}
            />
          ))}
          {uploadingItems.map((u) => (
            <AttachmentItem
              key={u.id}
              mode="edit"
              item={{
                fileId: u.id,
                name: u.fileName,
                size: 0,
                uploadedAt: '',
                ext: '',
                uploading: true,
              }}
            />
          ))}
        </ListRoot>
      )}
    </AttachmentBlock>
  );
};

export default AttachmentField;
